import { Analytics } from '@util/Analytics';
import React, {
  ComponentType,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { isDefined, isEmpty, isNonEmptyString } from 'ts-utils';
import { AssessmentScreen, FlowConfig, ScreenConfigBase, ScreenType } from '../../config/FlowTypes';
import { AppSelector, store, useAppStore } from '../../store/Store';
import '../../theme.less';
import { Environment } from '../../util/Environment';
import { getScreenAnswers } from '../../util/ConditionalNextScreenSelectors';
import { SpinnerOverlay } from '../functional/SpinnerOverlay';
import { ToastNotification } from '../functional/ToastNotification';
import { AppContainer } from '../styles/AppContainer';
import { ProgressHeader } from '../styles/ProgressHeader';
import { CONFIGURABLE_SCREENS } from './ConfigurableScreens';
import { FlowRoute, NavigationProps, NextOptions, useFlowNavigator } from './FlowNavigation';

type Props = {
  config: FlowConfig<AssessmentScreen>;
  onChangeBackdropColor?: Dispatch<SetStateAction<string | undefined>>;
};

type ToastLayoutConfig = {
  toastMessage?: string;
  toastDuration?: number;
};

export const FlowStack: FunctionComponent<Props> = (props) => {
  const { config, onChangeBackdropColor } = props;
  const currentPage = useAppStore(AppSelector.currentPage);
  const baseNavigation = useFlowNavigator();

  const screenConfig = config.screens.find((screen) => screen.screenName === currentPage);
  if (screenConfig === undefined) {
    throw new Error(`Screen ${currentPage} was not found in the flow config`);
  }

  const showHeader = screenConfig.showHeaderBar ?? true;
  const showProgress = config.showProgressBar && config.screens.length > 0;

  const [toastVisible, setToastVisible] = useState(false);
  const [activeToastConfig, setActiveToastConfig] = useState<{
    message: string;
    duration: number;
  } | null>(null);

  const [spinnerOverlayVisible, setSpinnerOverlayVisible] = useState(false);

  useEffect(
    function updateBackdropColor() {
      if (!isEmpty(screenConfig.backdropColor) && onChangeBackdropColor === undefined) {
        console.error(
          `${currentPage} has a backdropColor but no onChangeBackdropColor prop was passed to FlowStack`
        );
      } else {
        onChangeBackdropColor?.(screenConfig.backdropColor);
      }
    },
    [screenConfig.backdropColor, onChangeBackdropColor, currentPage]
  );

  useEffect(function trackStartEvent() {
    if (config.startEvent !== undefined) {
      Analytics.track(config.startEvent);
    }
  }, []);

  useEffect(
    function trackScreenViewEvent() {
      Analytics.screen(screenConfig.screenName);
    },
    [screenConfig.screenName]
  );

  useEffect(
    function onScreenChange() {
      window.scrollTo(0, 0);
      // Don't reset toast on screen change - let it complete naturally
    },
    [screenConfig.screenName]
  );

  useEffect(
    function resetHistoryOnEntry() {
      if (screenConfig.resetHistoryOnEntry === true) {
        baseNavigation.goTo(screenConfig.screenName, true);
      }
    },
    [screenConfig.screenName]
  );

  // Derive toast config from current screen layout
  const layout = (
    screenConfig as ScreenConfigBase<ScreenType, string> & { layout?: ToastLayoutConfig }
  ).layout;
  const toastConfig =
    layout && isNonEmptyString(layout.toastMessage)
      ? { message: layout.toastMessage, duration: layout.toastDuration ?? 1000 }
      : null;

  // Wrap navigation to handle spinner overlay
  const navigation = {
    ...baseNavigation,
    next: <T extends string>(routeProps: FlowRoute<T>, options?: NextOptions<string>) => {
      const spinnerConfig = screenConfig.spinnerOverlay;

      if (isDefined(spinnerConfig)) {
        const selection = getScreenAnswers(store.getState(), screenConfig.screenName)?.[0] ?? '';

        const shouldShow = isDefined(spinnerConfig.showWhen)
          ? spinnerConfig.showWhen(selection)
          : true;

        if (shouldShow === true) {
          setSpinnerOverlayVisible(true);
          setTimeout(() => {
            setSpinnerOverlayVisible(false);
            baseNavigation.next(routeProps, options);
          }, spinnerConfig.durationMs);
          return;
        }
      }

      // No spinner overlay - navigate immediately
      baseNavigation.next(routeProps, options);
    },
  };

  const ScreenComponent = CONFIGURABLE_SCREENS[screenConfig.type] as ComponentType<
    NavigationProps<ScreenConfigBase<ScreenType, string>, string>
  >;

  const screenProps = {
    key: screenConfig.screenName,
    navigation,
    ...screenConfig,
    ...(toastConfig && {
      onShowToast: () => {
        setActiveToastConfig(toastConfig);
        setToastVisible(true);
      },
    }),
  };

  return (
    <>
      {showHeader === true && (
        <ProgressHeader config={config} currentScreen={currentPage} showProgress={showProgress} />
      )}
      {/** The data-test-msh attribute is used by Cypress Autopilot test to identify screen type and name.
       * Changes made to this attribute may break the tests. */}
      <AppContainer
        data-test-msh={Environment.APP + '-' + screenConfig.type + '-' + screenConfig.screenName}
      >
        {/* Using component `key` so that it's forced to remount when the screen changes. */}
        <ScreenComponent {...screenProps} />
      </AppContainer>
      {/* Flow-level toast notification */}
      {activeToastConfig && (
        <ToastNotification
          message={activeToastConfig.message}
          isVisible={toastVisible}
          duration={activeToastConfig.duration}
          onComplete={() => {
            setToastVisible(false);
            setActiveToastConfig(null);
          }}
        />
      )}
      {/* Spinner overlay */}
      {spinnerOverlayVisible && <SpinnerOverlay />}
    </>
  );
};
